import { shortSpinId } from '../../../Model/Spin/spinId.js'
import React, { useMemo, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import { Card, KpiTile } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import { spinDetailPath, taxonLabel, tsLabel } from './spinMetrics.logic.js'
import { num, pct, refDetailPath } from './spinRefDetail.logic.js'
import {
  DEFAULT_SINGLETON_IDENTITY,
  SINGLETON_IDENTITY_OPTIONS,
  cohortSentence,
  parseSingletonIdentity,
  singletonCohortContext,
  singletonTruncationNote
} from './spinSingletons.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinHighIdentitySingletons, useSpinMetrics } from './useSpinData.js'

/** Row caps offered for the capped list. Raising it makes the query slower, not the cohort bigger. */
const LIMIT_OPTIONS = [100, 250, 500, 1000]

/**
 * The mirror image of the reference page: instead of "does SPIN split one reference id across clusters?",
 * this asks "did SPIN leave a near-perfect reference match sitting entirely alone?" — current SPINs with
 * exactly ONE member OTU whose NCBI blast row cleared the identity floor.
 *
 * Two things this page must keep straight, because both invite a wrong reading:
 *  1. Cluster size and reference identity are strongly correlated on this data (singleton members average
 *     91.8% identity, 3.9% at ≥ 99.5%; SPINs of 21+ members average 96.2%, 53.7%). So MOST singletons are
 *     just sequences with weak reference evidence — expected, not actionable. Only the high-identity slice
 *     is a question, which is why the floor is not optional here.
 *  2. That correlation is partly reference-DB coverage, not clustering quality: common taxa are both
 *     better represented in NCBI and more abundant. Read it as "which clusters rest on solid evidence",
 *     never as "SPIN clusters high-identity sequences better".
 *
 * OPT-IN: the endpoint is ~3.3s (singleton-ness is a property of the whole current membership), so nothing
 * is fetched until the user asks. The whole-set metrics used for the denominators are the cheap query and
 * do load on mount.
 */
export const SpinSingletonsPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  const [params] = useSearchParams()
  const qs = params.toString()
  const suffix = qs ? `?${qs}` : ''

  // `picked` is what the controls show; the hook only ever holds an answer for the loaded question, and
  // clears itself the moment either control changes — so the table can never disagree with its label.
  const [thresholdRaw, setThresholdRaw] = useState(String(DEFAULT_SINGLETON_IDENTITY))
  const [limit, setLimit] = useState(LIMIT_OPTIONS[0]!)
  const threshold = parseSingletonIdentity(thresholdRaw)

  const { metrics, loading: metricsLoading } = useSpinMetrics(selected)
  const { singletons, loading, error, load } = useSpinHighIdentitySingletons(selected, threshold, limit)

  const rows = singletons?.singletons ?? []
  const ctx = useMemo(
    () => singletonCohortContext(singletons?.totalCount ?? 0, metrics?.sizeDistribution, metrics?.snapshot?.spinCount),
    [singletons, metrics]
  )
  const truncation = singletons ? singletonTruncationNote(singletons) : null

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · High-identity singletons" sel={sel} />

      <section className="space-y-3">
        <h2 className="text-xl font-semibold">
          Clusters of one that match a reference almost perfectly
          <InfoTip tip="A row is a current SPIN with exactly ONE member OTU whose NCBI blast row has hit_percentage >= the floor below. Singletons whose member has no NCBI blast row at all are out of scope — there is no identity to judge them by (6.5% of current members have no NCBI row)." />
        </h2>

        <p className="text-sm text-gray-600 max-w-4xl">
          Two thirds of current SPINs hold a single member, and that is mostly unremarkable: singleton members average{' '}
          <b>91.8% NCBI identity</b> with only <b>3.9%</b> at ≥ {DEFAULT_SINGLETON_IDENTITY}%, versus <b>96.2%</b> / <b>53.7%</b> for
          SPINs of 21+ members. Weak reference evidence produces lonely clusters, and nothing needs fixing there. The cohort on this page
          is the exception: an <i>almost-perfect</i> match to a known reference, and yet the sequence clusters entirely alone.
        </p>

        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-1 text-sm">
            <span className="text-gray-500">
              NCBI identity floor
              <InfoTip
                tip={`Keeps only singletons whose one member matched an NCBI reference at least this well. There is deliberately no "no floor" option: without one the cohort is simply all ~291k singletons and says nothing.`}
              />
            </span>
            <select className="select select-bordered select-xs" value={thresholdRaw} onChange={(e) => setThresholdRaw(e.target.value)}>
              {SINGLETON_IDENTITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-sm">
            <span className="text-gray-500">
              Rows
              <InfoTip tip="Caps the returned list only — the cohort size (totalCount) is unaffected. The cap is applied by the backend after ordering by reads DESC, so raising it reveals more of the same ranking." />
            </span>
            <select
              className="select select-bordered select-xs"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              {LIMIT_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-sm btn-primary" onClick={load} disabled={loading || !selected}>
            {loading ? 'Loading… (~3s)' : singletons ? 'Reload cohort (~3s)' : 'Load cohort (~3s)'}
          </button>
        </div>

        <p className="text-sm text-gray-500 max-w-4xl">
          Nothing is fetched until you press Load. This query takes <b>~3 seconds</b> and there is no way around it: whether a SPIN is a
          singleton can only be decided over the <i>whole</i> current membership (latest-wins by <span className="font-mono">assigned_at</span>),
          so there is no reference id to seed from and no index that helps. Changing either control above clears the result — press Load
          again to ask the new question.
        </p>

        <p className="text-sm text-warning max-w-4xl">
          <b>Caveat.</b> The size↔identity correlation above is partly <i>reference-database coverage</i>, not clustering quality: common
          taxa are both better represented in NCBI and more abundant in samples. Read this page as “which clusters rest on solid reference
          evidence”, <b>not</b> as “SPIN clusters high-identity sequences better”.
        </p>
      </section>

      {error && <p className="text-error">Could not load high-identity singletons: {error}</p>}

      {singletons && (
        <section className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiTile
              label={
                <>
                  High-identity singletons
                  <InfoTip tip="Current SPINs with exactly one member whose NCBI blast row cleared the floor. The true cohort size, independent of the row cap below." />
                </>
              }
              value={num(singletons.totalCount)}
            />
            <KpiTile
              label={
                <>
                  All single-member SPINs
                  <InfoTip tip="Whole-set count of SPINs with exactly one current member, read from the size distribution (the cheap metrics query). The denominator that keeps the cohort count honest." />
                </>
              }
              value={metricsLoading && ctx.singletons === 0 ? '…' : num(ctx.singletons)}
            />
            <KpiTile
              label={
                <>
                  Cohort share of singletons
                  <InfoTip tip="Cohort ÷ all single-member SPINs. Small by construction: most singletons have weak reference evidence, so they never clear the floor." />
                </>
              }
              value={ctx.shareOfSingletons == null ? '—' : pct(ctx.shareOfSingletons)}
            />
            <KpiTile
              label={
                <>
                  Identity floor in force
                  <InfoTip tip="The minHitPercentage the loaded result was computed with — echoed straight back by the backend, not the control's current value." />
                </>
              }
              value={`≥ ${singletons.minHitPercentage}%`}
            />
          </div>

          <p className="text-sm text-gray-600 max-w-4xl">
            {cohortSentence(ctx, singletons.minHitPercentage)} Set:{' '}
            <b>{singletons.amplicon ?? 'all amplicons'}</b> as of {tsLabel(singletons.asOf)} ({singletons.algorithmType}).
          </p>

          <Card title={`Cohort — ${num(singletons.returnedCount)} shown, ordered by reads DESC`}>
            {truncation && (
              <p className="text-sm text-warning mb-2">
                <b>{truncation}</b>
                <InfoTip tip="The cap is applied server-side after ordering by summed reads DESC. Identity saturates at 100% in this cohort, so ordering by identity would return an arbitrary slice; abundance is what makes a capped window meaningful. Raise Rows above to see more." />
              </p>
            )}
            {rows.length === 0 ? (
              <p className="text-gray-500 text-sm">
                No SPIN in this set holds a single member matching NCBI at ≥ {singletons.minHitPercentage}%. Lower the floor, or read this
                as good news.
              </p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-2">
                  Not re-sortable on purpose: the cap is applied by the backend, so re-sorting the page would only reorder this visible
                  slice and quietly imply it is the whole cohort.
                </p>
                <div className="overflow-x-auto max-h-[32rem]">
                  <table className="table table-xs table-pin-rows">
                    <thead>
                      <tr>
                        <th>SPIN</th>
                        <th>
                          NCBI accession
                          <InfoTip tip="The accession the single member matched. Open it in the reference drill-down to see which other SPINs carry the same accession — the merge candidates." />
                        </th>
                        <th className="text-right">
                          NCBI identity
                          <InfoTip tip="hit_percentage from the member's NCBI blast row — always NCBI-sourced on this endpoint, never BOLD." />
                        </th>
                        <th className="text-right">
                          NCBI coverage
                          <InfoTip tip="Query coverage of that same NCBI blast row. High identity over LOW coverage is the classic false alarm: a short fragment can match a reference almost perfectly and still be a genuinely distinct sequence." />
                        </th>
                        <th>Genus / species</th>
                        <th className="text-right">
                          Reads
                          <InfoTip tip="Summed reads of the single member — the backend's ordering key, so the top of this list is the most abundant part of the cohort." />
                        </th>
                        <th className="text-right">
                          Core bp
                          <InfoTip tip="Core sequence length of the SPIN. Short cores are the other common innocent explanation for clustering alone." />
                        </th>
                        <th>Minted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.spinId}>
                          <td className="font-mono">
                            <Link to={spinDetailPath(r.spinId, suffix)} className="link link-primary">
                              {shortSpinId(r.spinId)}
                            </Link>
                          </td>
                          <td className="font-mono break-all">
                            <Link to={refDetailPath('NCBI_ID', r.ncbiId, suffix)} className="link link-primary">
                              {r.ncbiId}
                            </Link>
                          </td>
                          <td className="text-right tabular-nums">{pct(r.hitPercentage)}</td>
                          <td className="text-right tabular-nums">{pct(r.queryCoverage)}</td>
                          <td>{taxonLabel(r)}</td>
                          <td className="text-right tabular-nums">{num(r.reads)}</td>
                          <td className="text-right tabular-nums">{num(r.coreSequenceLength)}</td>
                          <td className="whitespace-nowrap">{tsLabel(r.mintedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </Card>

          <Card title="How to read a row">
            <div className="text-sm text-gray-600 space-y-2 max-w-4xl">
              <p>
                A near-perfect match to a known reference <b>plus</b> exactly one member means one of two things, and the row alone cannot
                tell you which:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <b>Genuinely unique despite the match.</b> A short or trimmed fragment, or a divergent region, can match a reference at
                  99.9% and still share too little with anything else to cluster. Low <i>coverage</i> or a short <i>core bp</i> in the row
                  is the tell.
                </li>
                <li>
                  <b>The clustering missed a merge.</b> Full-length, well-covered, and other sequences carrying the same accession sit in
                  other SPINs — that is a real should-have-merged candidate.
                </li>
              </ul>
              <p>
                Distinguish them from the SPIN detail page: open the <b>SPIN</b> link for its core sequence, amplicon and history, then
                follow the <b>NCBI accession</b> link into reference concordance for that same accession — if it is spread across other
                SPINs too, you are looking at a split, not a genuinely lonely sequence.
              </p>
              <p className="text-gray-500">
                Out of scope by definition: singletons whose member has <b>no</b> NCBI blast row (6.5% of current members) — there is no
                identity to judge them by, so they can neither clear nor fail the floor.
              </p>
            </div>
          </Card>
        </section>
      )}
    </div>
  )
}
